import { useContext, useMemo } from 'react'

import {
  GetAnnotationDotsQueryVariables,
  GetAnnotationsQueryVariables,
} from '@sketch/gql-types'

import {
  AnnotationQueryVariablesContext,
  AnnotationQueryVariablesContextValue,
} from '../context'

type AnnotationVariablesFormat = 'overlay' | 'sidebar' | 'default'

const formatSubject = (context: AnnotationQueryVariablesContextValue) => {
  const { permanentArtboardId, permanentPageId } = context

  if (permanentArtboardId) {
    return { type: 'ARTBOARD' as const, permanentId: permanentArtboardId }
  }

  return { type: 'PAGE' as const, permanentId: permanentPageId! }
}

export const formatOverlayVariables = (
  context: AnnotationQueryVariablesContextValue
) => {
  const { shareIdentifier, versionIdentifier, annotationStatus } = context
  const queryVariables: GetAnnotationDotsQueryVariables = {
    shareIdentifier,
    subject: formatSubject(context),
    versionIdentifier,
    annotationStatus,
  }

  return queryVariables
}

export const formatSidebarVariables = (
  context: AnnotationQueryVariablesContextValue
) => {
  const {
    shareIdentifier,
    versionIdentifier,
    annotationStatus,
    annotationSort: sort,
  } = context

  const queryVariables: GetAnnotationsQueryVariables = {
    shareIdentifier,
    subject: formatSubject(context),
    versionIdentifier,
    annotationStatus,
    sort,
  }

  return queryVariables
}

export const formatDefaultVariables = (
  context: AnnotationQueryVariablesContextValue
) => context

const formatterByFormat = {
  overlay: formatOverlayVariables,
  sidebar: formatSidebarVariables,
  default: formatDefaultVariables,
} as const

export const useAnnotationQueryVariables = <
  T extends AnnotationVariablesFormat
>(
  format: T
) => {
  const contextValue = useContext(AnnotationQueryVariablesContext)

  if (!contextValue) {
    throw new Error(
      '`useAnnotionQueryVariables` must be used with the `AnnotationQueryVariablesProvider`'
    )
  }

  const formatter = formatterByFormat[format]

  return useMemo(
    () => formatter(contextValue) as ReturnType<typeof formatterByFormat[T]>,
    [contextValue, formatter]
  )
}
